<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {TimeRange, formatTime, toMinutes, toTime} from "@/team/planning";
import {formatMinutes} from "@/planning/lib/weekPlanning";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {Textarea} from "@/core/components/ui/textarea";
import {ArrowDown, ArrowUp, Equal} from "@lucide/vue";

const STEP_MINUTES = 15;

/** Short reasons offered as chips, free note aside. */
const REASONS = ['Retard', 'Départ anticipé', 'Remplacement', 'Formation', 'Rendez-vous', 'Autre'];

const {open, range, original} = defineProps<{
    open: boolean;
    memberName: string;
    dayLabel: string;
    /** The time range being edited, as proposed (click or resize). */
    range: TimeRange | null;
    /** The range as currently planned, for the duration delta. */
    original: TimeRange | null;
}>();

const emits = defineEmits<{
    (event: 'update:open', open: boolean): void;
    (event: 'save', payload: { starts_at: string; ends_at: string; reason: string; note: string }): void;
}>();

const start = ref<number>(9 * 60);
const end = ref<number>(19 * 60);
const reason = ref<string>('');
const note = ref<string>('');
const reasonMissing = ref<boolean>(false);
const saving = ref<boolean>(false);

watch(() => [open, range], () => {
    if (open && range) {
        start.value = toMinutes(range.starts_at);
        end.value = toMinutes(range.ends_at);
        reason.value = '';
        note.value = '';
        reasonMissing.value = false;
        saving.value = false;
    }
}, {immediate: true});

const nudgeStart = (direction: 1 | -1) => {
    start.value = Math.min(Math.max(start.value + direction * STEP_MINUTES, 0), end.value - STEP_MINUTES);
};

const nudgeEnd = (direction: 1 | -1) => {
    end.value = Math.min(Math.max(end.value + direction * STEP_MINUTES, start.value + STEP_MINUTES), 23 * 60 + 45);
};

const duration = computed(() => end.value - start.value);

const delta = computed(() => {
    if (!original) {
        return 0;
    }

    return duration.value - (toMinutes(original.ends_at) - toMinutes(original.starts_at));
});

const save = () => {
    if (!reason.value) {
        reasonMissing.value = true;

        return;
    }

    saving.value = true;
    emits('save', {
        starts_at: toTime(start.value),
        ends_at: toTime(end.value),
        reason: reason.value,
        note: note.value.trim(),
    });
};
</script>

<template>
  <Dialog :open="open" @update:open="value => emits('update:open', value)">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>Modifier la plage horaire</DialogTitle>
        <p class="text-xs text-gray-400">
          {{ memberName }} · {{ dayLabel }}
        </p>
      </DialogHeader>

      <div class="flex gap-3">
        <div v-for="bound in (['start', 'end'] as const)" :key="bound" class="flex-1 rounded-xl bg-gray-50 px-3.5 py-3">
          <div class="mb-1.5 text-[10px] font-semibold text-gray-400">
            {{ bound === 'start' ? 'Début' : 'Fin' }}
          </div>
          <div class="flex items-center gap-2">
            <button
              class="flex size-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:border-primary/40"
              @click="bound === 'start' ? nudgeStart(-1) : nudgeEnd(-1)"
            >
              −
            </button>
            <div class="flex-1 text-center text-sm font-bold">
              {{ formatTime(toTime(bound === 'start' ? start : end)) }}
            </div>
            <button
              class="flex size-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:border-primary/40"
              @click="bound === 'start' ? nudgeStart(1) : nudgeEnd(1)"
            >
              +
            </button>
          </div>
        </div>
      </div>

      <div
        class="flex items-center gap-2.5 rounded-lg px-3 py-2"
        :class="delta === 0 ? 'bg-gray-50' : delta > 0 ? 'bg-green-50' : 'bg-amber-50'"
      >
        <component
          :is="delta === 0 ? Equal : delta > 0 ? ArrowUp : ArrowDown"
          class="size-4"
          :class="delta === 0 ? 'text-gray-400' : delta > 0 ? 'text-green-700' : 'text-amber-700'"
        />
        <span class="flex-1 text-[11px] text-gray-500">
          Durée : <b class="text-gray-800">{{ formatMinutes(duration) }}</b>
        </span>
        <span
          class="text-xs font-bold"
          :class="delta === 0 ? 'text-gray-400' : delta > 0 ? 'text-green-700' : 'text-amber-700'"
        >
          {{ delta === 0 ? 'Durée inchangée' : `${delta > 0 ? '+' : '−'}${formatMinutes(Math.abs(delta))}` }}
        </span>
      </div>

      <div>
        <div class="mb-1.5 text-[10px] font-bold text-gray-400">
          MOTIF DE LA MODIFICATION
        </div>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="option in REASONS"
            :key="option"
            class="h-7 rounded-lg border px-2.5 text-[11px] font-semibold transition-colors"
            :class="reason === option
              ? 'border-primary bg-primary text-white'
              : reasonMissing
                ? 'border-red-300 bg-white text-gray-600'
                : 'border-gray-200 bg-white text-gray-600 hover:border-primary/40'"
            @click="reason = option; reasonMissing = false"
          >
            {{ option }}
          </button>
        </div>
        <p v-if="reasonMissing" class="mt-1 text-xs text-red-500">
          Choisissez un motif.
        </p>
      </div>

      <Textarea
        v-model="note"
        placeholder="Précision (optionnel) — ex. retard de train, remplacement de Léa…"
        class="min-h-14 text-xs"
      />

      <div class="flex justify-end gap-2">
        <BaseButton @click="emits('update:open', false)">
          Annuler
        </BaseButton>
        <BaseButton primary :loading="saving" @click="save">
          Enregistrer
        </BaseButton>
      </div>
    </DialogContent>
  </Dialog>
</template>
